import React, { useState, useEffect } from 'react';
import {
    Container,
    Paper,
    Typography,
    Grid,
    Box,
    TextField,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    Card,
    CardContent,
    Avatar,
    useTheme,
    useMediaQuery
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import axios from '../../axios';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import SearchIcon from '@mui/icons-material/Search';
import PrintIcon from '@mui/icons-material/Print';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import GroupIcon from '@mui/icons-material/Group';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import PaymentsIcon from '@mui/icons-material/Payments';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import StoreIcon from '@mui/icons-material/Store';

const DailyMovementReport = () => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [loading, setLoading] = useState(false);
    const [date, setDate] = useState(dayjs().format('YYYY-MM-DD'));
    const [reportData, setReportData] = useState(null);

    const fetchReport = async () => {
        setLoading(true);
        try {
            const response = await axios.get('/AccountingReports/daily-movement', {
                params: { date }
            });
            setReportData(response.data);
        } catch (error) {
            console.error('Error fetching daily movement report:', error);
            toast.error('فشل في تحميل التقرير');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchReport();
    }, []);

    const handlePrint = () => {
        window.print();
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount || 0);
    };

    const SummaryCard = ({ title, value, icon, color }) => (
        <Card sx={{ 
            height: '100%', 
            borderRadius: 4, 
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            position: 'relative',
            overflow: 'hidden',
            '&::after': {
                content: '""',
                position: 'absolute',
                top: 0,
                right: 0,
                width: '6px',
                height: '100%',
                background: color
            }
        }}>
            <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Avatar sx={{ 
                        bgcolor: `${color}15`, 
                        color: color,
                        width: 40,
                        height: 48,
                        mr: 2
                    }}>
                        {icon}
                    </Avatar>
                    <Typography color="text.secondary" variant="subtitle2" fontWeight="600">
                        {title}
                    </Typography>
                </Box>
                <Typography variant="h5" fontWeight="800" sx={{ color: 'text.primary' }}>
                    {formatCurrency(value)}
                    <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
                        ر.س
                    </Typography>
                </Typography>
            </CardContent>
        </Card>
    );

    return (
        <Container maxWidth="xl" sx={{ py: 4 }}>
            {/* Header Area */}
            <Box className="no-print" sx={{ 
                mb: 4, 
                display: 'flex', 
                flexDirection: isMobile ? 'column' : 'row',
                justifyContent: 'space-between', 
                alignItems: isMobile ? 'stretch' : 'center',
                gap: 2
            }}>
                <Box>
                    <Typography variant="h4" fontWeight="800" sx={{ color: theme.palette.primary.dark }}>
                        تقرير الحركة اليومية
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        متابعة المبيعات، المشتريات، المقبوضات والمصروفات اليومية
                    </Typography>
                </Box>
                
                <Paper elevation={0} sx={{ 
                    p: 1.5, 
                    borderRadius: 3, 
                    display: 'flex', 
                    gap: 1.5,
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'background.paper'
                }}>
                    <TextField
                        type="date"
                        value={date}
                        onChange={(e) => setDate(e.target.value)}
                        size="small"
                        sx={{ minWidth: 150 }}
                    />
                    <Button 
                        variant="contained" 
                        startIcon={<SearchIcon />} 
                        onClick={fetchReport} 
                        disabled={loading}
                        sx={{ borderRadius: 2, px: 3 }}
                    >
                        تحديث
                    </Button>
                    <Button 
                        variant="outlined" 
                        startIcon={<PrintIcon />} 
                        onClick={handlePrint}
                        sx={{ borderRadius: 2 }}
                    >
                        طباعة
                    </Button>
                </Paper>
            </Box>

            {loading ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 15 }}>
                    <CircularProgress size={60} thickness={4} />
                    <Typography sx={{ mt: 2 }} color="text.secondary">جاري جلب البيانات...</Typography>
                </Box>
            ) : reportData ? (
                <Box id="printable-report">
                    <Grid container spacing={3}>
                        {/* Summary Section */}
                        <Grid item xs={12} sm={6} md={2.4}>
                            <SummaryCard 
                                title="إجمالي المبيعات" 
                                value={reportData.totalSales} 
                                icon={<ShoppingCartIcon />} 
                                color="#1976d2" 
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={2.4}>
                            <SummaryCard 
                                title="إجمالي المشتريات" 
                                value={reportData.totalPurchases} 
                                icon={<ShoppingBagIcon />} 
                                color="#f57c00" 
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={2.4}>
                            <SummaryCard 
                                title="إجمالي المقبوضات" 
                                value={reportData.totalIncomeVouchers} 
                                icon={<TrendingUpIcon />} 
                                color="#2e7d32" 
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={2.4}>
                            <SummaryCard 
                                title="إجمالي المصروفات" 
                                value={reportData.totalExpenseVouchers} 
                                icon={<TrendingDownIcon />} 
                                color="#d32f2f" 
                            />
                        </Grid>
                        <Grid item xs={12} sm={12} md={2.4}>
                            <Card sx={{ 
                                height: '100%', 
                                borderRadius: 4, 
                                background: reportData.netMovement >= 0 
                                    ? 'linear-gradient(135deg, #2e7d32 0%, #4caf50 100%)' 
                                    : 'linear-gradient(135deg, #c62828 0%, #ef5350 100%)',
                                color: 'white',
                                boxShadow: '0 8px 25px rgba(0,0,0,0.1)'
                            }}>
                                <CardContent>
                                    <Typography variant="subtitle2" fontWeight="600" sx={{ opacity: 0.9, mb: 2 }}>
                                        صافي الحركة
                                    </Typography>
                                    <Typography variant="h5" fontWeight="800">
                                        {formatCurrency(reportData.netMovement)}
                                    </Typography>
                                    <Typography variant="caption" sx={{ opacity: 0.8 }}>
                                        رصيد السيولة لليوم
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>

                        {/* Analysis Section */}
                        <Grid item xs={12} md={4}>
                            <Paper sx={{ p: 0, borderRadius: 4, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
                                <Box sx={{ p: 2, bgcolor: '#f8f9fa', borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <PaymentsIcon color="primary" />
                                    <Typography variant="h6" fontWeight="700">تحليل المبيعات</Typography>
                                </Box>
                                <TableContainer>
                                    <Table>
                                        <TableBody>
                                            <TableRow hover>
                                                <TableCell sx={{ fontWeight: 600 }}>مبيعات نقدي</TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 700, color: '#2e7d32' }}>{formatCurrency(reportData.totalCashSales)}</TableCell>
                                            </TableRow>
                                            <TableRow hover>
                                                <TableCell sx={{ fontWeight: 600 }}>مبيعات بنك / شبكة</TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 700, color: '#1976d2' }}>{formatCurrency(reportData.totalBankSales)}</TableCell>
                                            </TableRow>
                                            <TableRow hover>
                                                <TableCell sx={{ fontWeight: 600 }}>مبيعات آجل</TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 700, color: '#f57c00' }}>{formatCurrency(reportData.totalCreditSales)}</TableCell>
                                            </TableRow>
                                            <TableRow sx={{ bgcolor: '#f1f3f4' }}>
                                                <TableCell sx={{ fontWeight: 800 }}>إجمالي المبيعات</TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 800 }}>{formatCurrency(reportData.totalSales)}</TableCell>
                                            </TableRow>
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Paper>
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <Paper sx={{ p: 0, borderRadius: 4, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
                                <Box sx={{ p: 2, bgcolor: '#f8f9fa', borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <ShoppingBagIcon color="secondary" />
                                    <Typography variant="h6" fontWeight="700">تحليل المشتريات</Typography>
                                </Box>
                                <TableContainer>
                                    <Table>
                                        <TableBody>
                                            <TableRow hover>
                                                <TableCell sx={{ fontWeight: 600 }}>مشتريات نقدي</TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 700, color: '#d32f2f' }}>{formatCurrency(reportData.totalCashPurchases)}</TableCell>
                                            </TableRow>
                                            <TableRow hover>
                                                <TableCell sx={{ fontWeight: 600 }}>مشتريات بنك / شبكة</TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 700, color: '#1976d2' }}>{formatCurrency(reportData.totalBankPurchases)}</TableCell>
                                            </TableRow>
                                            <TableRow hover>
                                                <TableCell sx={{ fontWeight: 600 }}>مشتريات آجل</TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 700, color: '#f57c00' }}>{formatCurrency(reportData.totalCreditPurchases)}</TableCell>
                                            </TableRow>
                                            <TableRow sx={{ bgcolor: '#f1f3f4' }}>
                                                <TableCell sx={{ fontWeight: 800 }}>إجمالي المشتريات</TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 800 }}>{formatCurrency(reportData.totalPurchases)}</TableCell>
                                            </TableRow>
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Paper>
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <Paper sx={{ p: 0, borderRadius: 4, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
                                <Box sx={{ p: 2, bgcolor: '#f8f9fa', borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <AccountBalanceWalletIcon color="primary" />
                                    <Typography variant="h6" fontWeight="700">أرصدة الصناديق والبنوك</Typography>
                                </Box>
                                <TableContainer sx={{ maxHeight: 300 }}>
                                    <Table stickyHeader size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 700, bgcolor: '#fff' }}>الحساب</TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 700, bgcolor: '#fff' }}>رصيد أول</TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 700, bgcolor: '#fff' }}>الحركة</TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 700, bgcolor: '#fff' }}>الإغلاق</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {reportData.cashAndBankBalances?.map((acc, index) => (
                                                <TableRow key={index} hover>
                                                    <TableCell sx={{ fontWeight: 500 }}>{acc.accountName}</TableCell>
                                                    <TableCell align="right">{formatCurrency(acc.openingBalance)}</TableCell>
                                                    <TableCell align="right" sx={{ 
                                                        color: (acc.debit - acc.credit) >= 0 ? 'success.main' : 'error.main',
                                                        fontWeight: 600
                                                    }}>
                                                        {(acc.debit - acc.credit) > 0 ? '+' : ''}{formatCurrency(acc.debit - acc.credit)}
                                                    </TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 700 }}>{formatCurrency(acc.closingBalance)}</TableCell>
                                                </TableRow>
                                            ))}
                                            {(reportData.cashAndBankBalances?.length === 0 || !reportData.cashAndBankBalances) && (
                                                <TableRow>
                                                    <TableCell colSpan={4} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                                                        لا توجد حركات نقدية لهذا اليوم
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Paper>
                        </Grid>

                        {/* Customer Balances */}
                        <Grid item xs={12} md={4}>
                            <Paper sx={{ p: 0, borderRadius: 4, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
                                <Box sx={{ p: 2, bgcolor: '#f8f9fa', borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <GroupIcon sx={{ color: '#1976d2' }} />
                                    <Typography variant="h6" fontWeight="700">أرصدة العملاء</Typography>
                                </Box>
                                <TableContainer sx={{ maxHeight: 300 }}>
                                    <Table stickyHeader size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 700, bgcolor: '#fff' }}>العميل</TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 700, bgcolor: '#fff' }}>رصيد أول</TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 700, bgcolor: '#fff' }}>الحركة</TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 700, bgcolor: '#fff' }}>الإغلاق</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {reportData.customerBalances?.map((acc, index) => (
                                                <TableRow key={index} hover>
                                                    <TableCell sx={{ fontWeight: 500 }}>{acc.accountName}</TableCell>
                                                    <TableCell align="right">{formatCurrency(acc.openingBalance)}</TableCell>
                                                    <TableCell align="right" sx={{ 
                                                        color: (acc.debit - acc.credit) >= 0 ? 'success.main' : 'error.main',
                                                        fontWeight: 600
                                                    }}>
                                                        {(acc.debit - acc.credit) > 0 ? '+' : ''}{formatCurrency(acc.debit - acc.credit)}
                                                    </TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 700 }}>{formatCurrency(acc.closingBalance)}</TableCell>
                                                </TableRow>
                                            ))}
                                            {(reportData.customerBalances?.length === 0 || !reportData.customerBalances) && (
                                                <TableRow>
                                                    <TableCell colSpan={4} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                                                        لا توجد حركات للعملاء
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Paper>
                        </Grid>

                        {/* Supplier Balances */}
                        <Grid item xs={12} md={4}>
                            <Paper sx={{ p: 0, borderRadius: 4, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
                                <Box sx={{ p: 2, bgcolor: '#f8f9fa', borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <LocalShippingIcon sx={{ color: '#d32f2f' }} />
                                    <Typography variant="h6" fontWeight="700">أرصدة الموردين</Typography>
                                </Box>
                                <TableContainer sx={{ maxHeight: 300 }}>
                                    <Table stickyHeader size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 700, bgcolor: '#fff' }}>المورد</TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 700, bgcolor: '#fff' }}>رصيد أول</TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 700, bgcolor: '#fff' }}>الحركة</TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 700, bgcolor: '#fff' }}>الإغلاق</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {reportData.supplierBalances?.map((acc, index) => (
                                                <TableRow key={index} hover>
                                                    <TableCell sx={{ fontWeight: 500 }}>{acc.accountName}</TableCell>
                                                    <TableCell align="right">{formatCurrency(acc.openingBalance)}</TableCell>
                                                    <TableCell align="right" sx={{ 
                                                        color: (acc.debit - acc.credit) <= 0 ? 'success.main' : 'error.main', // Credit is normal for suppliers
                                                        fontWeight: 600
                                                    }}>
                                                        {(acc.debit - acc.credit) > 0 ? '+' : ''}{formatCurrency(acc.debit - acc.credit)}
                                                    </TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 700 }}>{formatCurrency(acc.closingBalance)}</TableCell>
                                                </TableRow>
                                            ))}
                                            {(reportData.supplierBalances?.length === 0 || !reportData.supplierBalances) && (
                                                <TableRow>
                                                    <TableCell colSpan={4} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                                                        لا توجد حركات للموردين
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Paper>
                        </Grid>
                    </Grid>
                </Box>
            ) : (
                <Box sx={{ textAlign: 'center', py: 10, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                    <StoreIcon sx={{ fontSize: 80, color: 'divider' }} />
                    <Typography color="text.secondary" variant="h6">
                        الرجاء اختيار تاريخ لعرض التقرير
                    </Typography>
                </Box>
            )}

            <style>
                {`
                @media print {
                    .no-print { display: none !important; }
                    body { padding: 0; margin: 0; background: white; }
                    #printable-report { width: 100%; padding: 20px; }
                    .MuiPaper-root { border: 1px solid #eee !important; box-shadow: none !important; }
                }
                `}
            </style>
        </Container>
    );
};

export default DailyMovementReport;
